import { gql, type TypedDocumentNode } from '@apollo/client';
import { useTransition } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { Position } from '#src/__generated__/globalTypes.ts';
import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { LabelL } from '#src/atoms/typography/index.tsx';
import { PlayerInformation } from '#src/components/HowToPlay/CreateATeam/PlayerInformation/index.tsx';
import {
  INITIAL_SELECTED_PLAYERS,
  type LineupPosition,
  type SelectedPlayers,
} from '#src/components/HowToPlay/CreateATeam/constants.ts';

import type { InstructionsContainer_anyPlayer } from './__generated__/index.graphql.ts';

const Root = styled(Vertical)`
  align-items: center;
  background: var(--c-nd-50);
  border-radius: var(--unit);
  padding: var(--double-unit);
`;

const DetailsContainer = styled(Vertical).attrs({ center: true })`
  width: 100%;
  background: var(--c-nd-50);
  border-radius: var(--double-unit);
  padding: var(--double-unit);
`;

type Props = {
  currentPositionData: any;
  currentPosition: LineupPosition | null;
  selectedPlayers: SelectedPlayers;
  setSelectedPlayers: (players: SelectedPlayers) => void;
  handleSetSelectedPlayers: (players: {
    [key in Position]?: string | null;
  }) => void;
  firstPlayerInfo: any;
  secondPlayerInfo: any;
  setSelectedCaptain: (captain: string | null) => void;
};

export const InstructionsContainer = ({
  currentPositionData,
  currentPosition,
  selectedPlayers,
  setSelectedPlayers,
  handleSetSelectedPlayers,
  firstPlayerInfo,
  secondPlayerInfo,
  setSelectedCaptain,
}: Props) => {
  const [isPending, startTransition] = useTransition();
  const handlePlayAgain = () => {
    setSelectedPlayers(INITIAL_SELECTED_PLAYERS);
    setSelectedCaptain(null);
  };
  if (currentPositionData?.position) {
    return (
      <Root>
        <LabelL bold>
          <FormattedMessage
            id="HowToPlay.CreateATeam.selectPosition"
            defaultMessage="Select your {position}"
            values={{ position: currentPositionData?.position }}
          />
        </LabelL>
        <DetailsContainer>
          <LabelL className="text-center">
            <FormattedMessage {...currentPositionData?.instructions} />
          </LabelL>
        </DetailsContainer>
        <PlayerInformation
          isPending={isPending}
          startTransition={startTransition}
          position={currentPosition}
          selectedPlayers={selectedPlayers}
          setSelectedPlayers={handleSetSelectedPlayers}
          playerInfo={firstPlayerInfo}
        />
        <PlayerInformation
          isPending={isPending}
          startTransition={startTransition}
          position={currentPosition}
          selectedPlayers={selectedPlayers}
          setSelectedPlayers={handleSetSelectedPlayers}
          playerInfo={secondPlayerInfo}
        />
      </Root>
    );
  }

  return (
    <Root>
      <LabelL bold>
        <FormattedMessage
          id="HowToPlay.CreateATeam.selectCaptain"
          defaultMessage="Select your captain"
        />
      </LabelL>
      <DetailsContainer>
        <LabelL className="text-center">
          <FormattedMessage
            id="HowToPlay.CreateATeam.selectCaptainDescription"
            defaultMessage="Select a captain for each team, this player will get 50% more points on their score, so choose him wisely!"
          />
        </LabelL>
      </DetailsContainer>
      <Button color="secondary" size="large" onClick={() => handlePlayAgain()}>
        <FormattedMessage
          id="HowToPlay.CreateATeam.playAgain"
          defaultMessage="Play again"
        />
      </Button>
    </Root>
  );
};

InstructionsContainer.fragments = {
  anyPlayer: gql`
    fragment InstructionsContainer_anyPlayer on AnyPlayerInterface {
      slug
      ...PlayerInformation_anyPlayer
    }
    ${PlayerInformation.fragments.anyPlayer}
  ` as TypedDocumentNode<InstructionsContainer_anyPlayer>,
};
